import { useCallback, useEffect, useState } from 'react';

import { GetStaticProps } from 'next';

import { PostOrPage, Tags } from '@tryghost/content-api';
import FuzzySearch from 'fuzzy-search';
import { NavitemRecord } from 'graphql/generated';

import BlogPostGrid from 'components/BlogPostGrid';
import CommonHead from 'components/CommonHead';
import ContentBox from 'components/ContentBox';
import Footer from 'components/Footer';
import Navigation from 'components/Navigation';
import RootContainer from 'components/RootContainer';
import SearchBar from 'components/SearchBar';
import Tag from 'components/Tag';

import api from 'lib/ghostSDK';
import { getNavItems } from 'lib/utils';

import * as S from 'styles/Blog.styles';

const Blog = ({
  blogPosts,
  tags,
  navItems
}: {
  blogPosts: PostOrPage[];
  tags: Tags;
  navItems: NavitemRecord[];
}) => {
  const [filteredBlogPosts, setFilteredBlogPosts] = useState(blogPosts);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchTermChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredBlogPosts(blogPosts);
      return;
    }

    const searcher = new FuzzySearch(blogPosts, ['title', 'authors.name', 'tags.name'], {
      caseSensitive: false,
      sort: true
    });
    const result = searcher.search(searchTerm);

    setFilteredBlogPosts(result);
  }, [searchTerm]);

  return (
    <>
      <CommonHead
        title='Blog - HackSoft - tailor-made software solutions for your business'
        description='While working on various projects, we gather first hand software development experience. This is where we share it with you!'
      />
      <Navigation {...{ navItems }} />
      <RootContainer>
        <ContentBox>
          <h1>Blog</h1>
          <p>
            While working on various projects, we gather first hand software development experience.
            This is where we share it with you!
          </p>
          <p>
            👉{' '}
            <a href='/blog/rss/' target='_blank'>
              RSS
            </a>
          </p>
        </ContentBox>

        <S.Tags>
          {tags.map((tag) => (
            <Tag tag={tag} key={tag.id} />
          ))}
        </S.Tags>
        <SearchBar handleChange={handleSearchTermChange} value={searchTerm} />
        <BlogPostGrid blogPosts={filteredBlogPosts} />
      </RootContainer>
      <Footer />
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  // Create API instance with site credentials
  const [blogPosts, tags] = await Promise.all([
    api.posts.browse({
      include: ['authors', 'tags'],
      limit: 'all',
      fields: [
        'id',
        'title',
        'slug',
        'feature_image',
        'visibility',
        'published_at',
        'primary_author',
        'custom_excerpt'
      ]
    }),
    api.tags.browse({
      limit: 'all',
      filter: ['visibility:public']
    })
  ]);
  const navigationItemsQuery = await getNavItems();
  const navigationItemsArray = navigationItemsQuery?.allNavitems;

  // Sort tags by the number on the first line of the description
  tags.sort(
    (a, b) =>
      parseInt(a.description?.split('\n')[0] || '') - parseInt(b.description?.split('\n')[0] || '')
  );

  return {
    props: {
      blogPosts,
      tags,
      navItems: navigationItemsArray
    },
    revalidate: 1
  };
};

export default Blog;
